// The element of the quote route. It asks presence what the quote is for
// before mounting either station, so the policy flow's hooks (pricing,
// flags, bootstrap, form clients) never run against a case quote.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { NetworkError } from "hooks/usePublicAPI";
import ErrorView from "pages/SavedQuote/ErrorView";

import ArrivalLoadingScreen from "./ArrivalLoadingScreen";
import { fetchQuotePresence } from "./api";
import { Frame, Message, useCaseQuoteShell } from "./CaseQuoteFrame";
import CaseQuoteStation from "./CaseQuoteStation";
import QuoteFlow from "./QuoteFlow";
import { pickQuoteStation, type QuoteStation } from "./quote-subject";

type Choice = { quoteId: string; station: QuoteStation | "error" } | null;

export default function QuoteSubjectRouter() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const [choice, setChoice] = useState<Choice>(null);
  const shell = useCaseQuoteShell();

  useEffect(() => {
    if (!quoteId) return;
    let cancelled = false;
    fetchQuotePresence(quoteId)
      .then((presence) => {
        if (!cancelled) {
          setChoice({
            quoteId,
            station: pickQuoteStation(presence.subject_type),
          });
        }
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // A quote not made yet is the policy flow's: it waits for the one
        // the pre-need form is creating. Any other failure says nothing of
        // the quote's kind, so neither station mounts.
        const notYetMade =
          error instanceof NetworkError && error.statusCode === 404;
        setChoice({ quoteId, station: notYetMade ? "policy" : "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [quoteId]);

  if (!quoteId) return <QuoteFlow />;

  if (choice === null || choice.quoteId !== quoteId) {
    return (
      <Frame shell={shell}>
        <Message shell={shell}>
          <ArrivalLoadingScreen />
        </Message>
      </Frame>
    );
  }
  if (choice.station === "error") {
    return (
      <Frame shell={shell}>
        <Message shell={shell}>
          <ErrorView />
        </Message>
      </Frame>
    );
  }
  if (choice.station === "case") return <CaseQuoteStation quoteId={quoteId} />;
  return <QuoteFlow />;
}
